import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Switch, InputNumber, message, Space, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { substituteConfigsAPI } from '../services/api';

const { TextArea } = Input;

// IP列表输入组件
const IPListInput = ({ value = [], onChange, placeholder }) => {
  const [inputValue, setInputValue] = useState('');

  const handleAdd = () => {
    if (!inputValue.trim()) return;
    
    // 按逗号分割并过滤空值
    const newIPs = inputValue
      .split(',')
      .map(ip => ip.trim())
      .filter(ip => ip.length > 0);
    
    if (newIPs.length > 0) {
      const updated = [...new Set([...value, ...newIPs])]; // 去重
      onChange(updated);
      setInputValue('');
    }
  };

  const handleRemove = (ipToRemove) => {
    onChange(value.filter(ip => ip !== ipToRemove));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  };

  return (
    <div>
      <Space.Compact style={{ width: '100%' }}>
        <Input
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onPressEnter={handleKeyPress}
          placeholder={placeholder}
        />
        <Button type="primary" onClick={handleAdd}>
          添加
        </Button>
      </Space.Compact>
      <div style={{ marginTop: 8, minHeight: 32 }}>
        {value.map((ip, index) => (
          <Tag
            key={index}
            closable
            onClose={() => handleRemove(ip)}
            style={{ marginTop: 4, marginBottom: 4 }}
          >
            {ip}
          </Tag>
        ))}
      </div>
    </div>
  );
};

const SubstituteConfigs = () => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    try {
      const response = await substituteConfigsAPI.getConfigs();
      setConfigs(response.data);
    } catch (error) {
      message.error('获取配置列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingConfig(null);
    form.resetFields();
    form.setFieldsValue({
      path: '/substitute-config.json',
      cache_ttl: 1296000,
      enable_command: 0,
      priority: 0,
      is_active: true,
      allowed_ips: [],
      ip_patterns: [],
      ua_patterns: [],
      patterns: '[]',
    });
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingConfig(record);
    
    // 解析 JSON 字符串为数组
    const parseJSONField = (field) => {
      if (!field) return [];
      if (typeof field === 'string') {
        try {
          return JSON.parse(field);
        } catch {
          return [];
        }
      }
      return Array.isArray(field) ? field : [];
    };

    form.setFieldsValue({
      ...record,
      // IP相关字段转换为数组
      allowed_ips: parseJSONField(record.allowed_ips),
      ip_patterns: parseJSONField(record.ip_patterns),
      ua_patterns: parseJSONField(record.ua_patterns),
      // 其他JSON字段保持字符串格式
      patterns: typeof record.patterns === 'string' ? record.patterns : JSON.stringify(record.patterns || []),
    });
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    Modal.confirm({
      title: '确认删除',
      content: '确定要删除这个配置吗？',
      onOk: async () => {
        try {
          await substituteConfigsAPI.deleteConfig(id);
          message.success('删除成功');
          fetchConfigs();
        } catch (error) {
          message.error('删除失败');
        }
      },
    });
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();

      // 将IP数组转换为JSON字符串
      if (Array.isArray(values.allowed_ips)) {
        values.allowed_ips = JSON.stringify(values.allowed_ips);
      }
      if (Array.isArray(values.ip_patterns)) {
        values.ip_patterns = JSON.stringify(values.ip_patterns);
      }
      if (Array.isArray(values.ua_patterns)) {
        values.ua_patterns = JSON.stringify(values.ua_patterns);
      }

      // 验证其他 JSON 格式
      try {
        if (values.patterns) JSON.parse(values.patterns);
      } catch (e) {
        message.error('JSON 格式错误，请检查 patterns 字段');
        return;
      }

      if (editingConfig) {
        await substituteConfigsAPI.updateConfig(editingConfig.id, values);
        message.success('更新成功');
      } else {
        await substituteConfigsAPI.createConfig(values);
        message.success('创建成功');
      }

      setModalVisible(false);
      fetchConfigs();
    } catch (error) {
      if (error.response) {
        const errorMsg = error.response.data?.error || '操作失败';
        // 检查是否是路径重复错误
        // 路径已存在的错误不再需要，因为现在允许同一路径有多个配置
        message.error(errorMsg);
      }
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '配置名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '访问路径',
      dataIndex: 'path',
      key: 'path',
      render: (path) => <code>{path}</code>,
    },
    {
      title: '版本',
      dataIndex: 'version',
      key: 'version',
      width: 100,
    },
    {
      title: '优先级',
      dataIndex: 'priority',
      key: 'priority',
      width: 80,
      sorter: (a, b) => a.priority - b.priority,
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 80,
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? '启用' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (date) => new Date(date).toLocaleString(),
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          添加配置
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={configs}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 20 }}
      />

      <Modal
        title={editingConfig ? '编辑配置' : '添加配置'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => setModalVisible(false)}
        width={800}
        okText="确定"
        cancelText="取消"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="配置名称"
            rules={[{ required: true, message: '请输入配置名称' }]}
          >
            <Input placeholder="例如：Googlebot配置" />
          </Form.Item>

            <Form.Item
              name="path"
              label="访问路径"
              rules={[
                { required: true, message: '请输入访问路径' },
                { pattern: /^\/.*\.json$/, message: '路径必须以 / 开头，以 .json 结尾' }
              ]}
              tooltip="例如：/substitute-config.json 或 /custom-config.json。注意：同一路径可以有多个配置，系统会根据访问者的 IP 和 User-Agent 自动匹配返回对应的配置。"
            >
              <Input placeholder="/substitute-config.json" />
            </Form.Item>

          <Form.Item
            name="version"
            label="版本号"
            rules={[{ required: true, message: '请输入版本号' }]}
          >
            <Input placeholder="例如：22298" />
          </Form.Item>

          <Form.Item
            name="cache_ttl"
            label="缓存TTL（秒）"
            rules={[{ required: true, message: '请输入缓存TTL' }]}
          >
            <InputNumber min={0} style={{ width: '100%' }} placeholder="1296000" />
          </Form.Item>

          <Form.Item
            name="enable_command"
            label="是否启用命令"
            valuePropName="checked"
            getValueFromEvent={(checked) => checked ? 1 : 0}
            getValueProps={(value) => ({ checked: value === 1 })}
          >
            <Switch />
          </Form.Item>

          <Form.Item
            name="content"
            label="内容"
          >
            <TextArea rows={3} placeholder="例如：google.com, pub-1486976040406674, DIRECT, f08c47fec0942fa0" />
          </Form.Item>

          <Form.Item
            name="allowed_ips"
            label="允许的IP列表"
            tooltip='输入IP地址，用逗号分隔多个IP（例如：127.0.0.1,192.168.1.1,66.249.）'
          >
            <IPListInput placeholder="例如：127.0.0.1,192.168.1.1,66.249." />
          </Form.Item>

          <Form.Item
            name="patterns"
            label="替换模式（JSON数组）"
            tooltip='例如：["s|adsbygoogle.js|adsbygoogle.js|inq"]'
          >
            <TextArea rows={3} placeholder='["s|adsbygoogle.js|adsbygoogle.js|inq"]' />
          </Form.Item>

          <Form.Item
            name="ip_patterns"
            label="访问IP白名单"
            tooltip='输入IP地址或CIDR，用逗号分隔（例如：127.0.0.1,192.168.1.0/24,66.249.）。只有白名单中的IP才能访问此配置。'
          >
            <IPListInput placeholder="例如：127.0.0.1,192.168.1.0/24,66.249." />
          </Form.Item>

          <Form.Item
            name="ua_patterns"
            label="访问User-Agent白名单"
            tooltip='输入User-Agent关键词，用逗号分隔（例如：Googlebot,Chrome）。只有白名单中的User-Agent才能访问此配置。IP和User-Agent是并且关系。'
          >
            <IPListInput placeholder="例如：Googlebot,Chrome" />
          </Form.Item>

          <Form.Item
            name="priority"
            label="优先级"
            tooltip="数字越大优先级越高，相同路径下会优先匹配优先级高的配置"
          >
            <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
          </Form.Item>

          <Form.Item
            name="is_active"
            label="是否启用"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SubstituteConfigs;

